import FullCalendar from "@fullcalendar/react";
import { Autocomplete, Box, Button, Card, Grid, Tooltip, styled, Dialog, DialogTitle, DialogContent, DialogActions, Alert, List, ListItem, ListItemText, Typography } from "@mui/material";
import { FC, useEffect, useRef, useState } from "react";
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin, { DateClickArg } from '@fullcalendar/interaction';
import { useTranslation } from "react-i18next";
import itLocale from '@fullcalendar/core/locales/it';
import deLocale from '@fullcalendar/core/locales/de';
import enLocale from '@fullcalendar/core/locales/en-gb';
import HeaderToolbar from "page-sections/calendar/HeaderToolbar";
import { EventClickArg, EventContentArg, EventInput } from "@fullcalendar/core";
import { calendarEntriesShiftTemplateApi, departmentsApi } from "api";
import { CalendarEntryShiftTemplateEntity, ShiftTemplateEntity } from "api/generated";
import FlexBox from "components/flexbox/FlexBox";
import ViewEventModalShift from "./ViewEventModal";
import { EventImpl } from "@fullcalendar/core/internal";
import AddEventModalShift from "./AddEventModal";
import { Department } from "pages/dashboards/dashboard-main-page";
import AppTextField from "components/input-fields/AppTextField";
import { t } from "i18next";
import { isHoliday } from "utils/holidays";
import { checkTokenRole } from "utils/checkToken";
import { useSeason } from "contexts/SeasonContext";
import AssignTemplateModalShift from "./AssignTemplateModalShift";
import { getColorFromId } from "utils/utils";
import toast from "react-hot-toast";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";


interface ExtendedCalendarEntryShiftTemplateEntity extends CalendarEntryShiftTemplateEntity {
    shiftTemplate: ShiftTemplateEntity;
    templateId: number;
}

interface BinAffectedAssignment {
    id: number;
    date: string;
    employee: { firstName: string; lastName: string };
    shiftDetail?: { workPosition?: { name: string } };
}

interface EventInputDepartmentId extends EventInput {
    departmentId?: number;
}


// styled component
const CalendarWrapper = styled(Box)(({ theme }) => ({
    padding: '1.5rem',
    '& .fc-theme-standard td, & .fc-theme-standard th': {
        border: 'none',
    },
    '& .fc-theme-standard, & .fc-scrollgrid': {
        border: 'none !important',
    },
    '& .fc .fc-scroller-harness-liquid': {
        marginTop: '1rem',
    },
    '& .fc th': {
        color: theme.palette.primary.main,
    },
    '& .fc-daygrid-day-top': {
        justifyContent: 'center',
        alignItems: 'center',
        height: 'inherit',
    },
    '& .fc .fc-daygrid-day.fc-day-today': {
        borderRadius: '8px',
        border: `5px solid ${theme.palette.primary.main}`,
        backgroundColor: 'transparent',
    },
    '& .fc-daygrid-day-number': {
        fontWeight: 600,
    },
    // Kalenderwoche marker in the top-left corner of each week's first cell
    '& .fc .fc-daygrid-week-number': {
        fontSize: 10,
        fontWeight: 600,
        padding: '2px 4px',
        borderRadius: '4px',
        color: theme.palette.text.secondary,
        backgroundColor: 'transparent',
    },
    '& .fc-h-event': {
        backgroundColor: theme.palette.primary.main,
        border: 'none',
        textAlign: 'left',
        borderRadius: '4px',
        padding: '0 8px',
        height: '30px',
        lineHeight: '20px',
        fontSize: '12px',
    },
    '& .fc-daygrid-day-bottom a': {
        backgroundColor: theme.palette.primary.main,
        display: 'block',
        textAlign: 'center',
        color: theme.palette.background.paper,
        fontSize: 10,
        fontWeight: 500,
    },
    '& .fc .fc-popover': {
        border: 'none',
        borderRadius: '8px',
        boxShadow: theme.shadows[1],
        zIndex: 111,
    },
    '& .fc .fc-popover-header': {
        backgroundColor: theme.palette.primary.main,
        color: 'white',
        fontSize: 12,
        fontWeight: 600,
        borderTopLeftRadius: '8px',
        borderTopRightRadius: '8px',
    },
    '& .fc .fc-daygrid-event-harness': {
        padding: 2,
        cursor: 'pointer',
    },
}));

const ShiftsCalendar: FC = () => {
    const calendarRef = useRef<FullCalendar>(null);
    const [initialEvents, setInitialEvents] = useState<EventInputDepartmentId[]>();
    const [allEvents, setAllEvents] = useState<EventInputDepartmentId[]>();
    const [date, setDate] = useState(new Date());
    const { i18n } = useTranslation();
    const isMobile = screen.width <= 768;
    const isTablet = screen.width <= 1024;
    const [departments, setDepartments] = useState<Department[]>([]);
    const [selectedDepartment, setSelectedDepartment] = useState<{ label: string; id: number; } | null>(null);
    const [assignModalState, setAssignModalState] = useState({ isOpened: false });
    const [allowOverwrite, setAllowOverwrite] = useState(false);
    const [binMode, setBinMode] = useState(false);
    const [pendingDelete, setPendingDelete] = useState<{ events: any[]; calendarApi: any; affected: BinAffectedAssignment[] } | null>(null);

    const { seasonId } = useSeason();
    const [viewEventState, setViewEventState] = useState<{
        isOpened: boolean;
        event?: EventImpl;
    }>({
        isOpened: false,
    });
    const [addEventState, setAddEventState] = useState<{
        isOpened: boolean;
        events?: EventInput[];
        startedOn?: Date;
    }>({
        isOpened: false,
        startedOn: new Date(),
    });

    const findSelectedLanguage = () => {
        switch (i18n.language) {
            case 'en':
                return enLocale;
            case 'it':
                return itLocale;
            case 'de':
                return deLocale;
            default:
                return enLocale;
        }
    };

    const nextMonth = () => {
        const calendarApi = calendarRef.current?.getApi();
        if (!calendarApi) {
            return;
        }
        calendarApi.next();
        setDate(calendarApi.getDate());
    };

    const prevMonth = () => {
        const calendarApi = calendarRef.current?.getApi();
        if (!calendarApi) {
            return;
        }
        calendarApi.prev();
        setDate(calendarApi.getDate());
    };

    const highlightToday = () => {
        const calendarApi = calendarRef.current?.getApi();
        if (!calendarApi) {
            return;
        }
        calendarApi.today();
        setDate(calendarApi.getDate());
    };

    // Marks public-holiday (festività) day numbers — national in red, regional (Südtirol) in blue.
    function renderDayCell(arg: { date: Date; dayNumberText: string }) {
        const holiday = isHoliday(arg.date);
        if (holiday) {
            return (
                <Tooltip title={holiday.name}>
                    <span style={{ color: holiday.region === 'SÜDTIROL' ? '#1e88e5' : '#e53935', fontWeight: 700 }}>
                        {arg.dayNumberText}
                    </span>
                </Tooltip>
            );
        }
        return <span>{arg.dayNumberText}</span>;
    }

    function renderEventContent(eventContent: EventContentArg) {
        const maxLength = 20;
        const eventTitle = eventContent.event.title;
        const truncatedTitle = eventTitle.length > maxLength ? `${eventTitle.substring(0, maxLength)}...` : eventTitle;
        const isGeneratedNegativeOne = eventContent.event._def.extendedProps?.isAutoGenerated !== -1;

        return isMobile ?
            (
                <Tooltip title={eventContent.event.title}>
                    <FlexBox style={{ fontSize: '10px', marginLeft: '-8px', fontStyle: isGeneratedNegativeOne ? "italic" : "normal", textDecoration: isGeneratedNegativeOne ? 'underline' : 'none' }}>{truncatedTitle}</FlexBox>
                </Tooltip>

            )
            :
            (
                <Tooltip title={eventContent.event.title}>
                    <FlexBox height={30} style={{ fontStyle: isGeneratedNegativeOne ? "italic" : "normal", textDecoration: isGeneratedNegativeOne ? 'underline' : 'none', }} alignItems={'center'}>
                        {truncatedTitle}
                    </FlexBox>
                </Tooltip>
            );
    }

    const fetchData = async () => {
        const role = checkTokenRole();
        const { data } = role === 'DEPCHIEF' ? await calendarEntriesShiftTemplateApi.findAllWhereIsBoss(seasonId)
            : await calendarEntriesShiftTemplateApi.findAll(seasonId);
        const events = data.map((entry: CalendarEntryShiftTemplateEntity) => {
            const extendedEntry = entry as ExtendedCalendarEntryShiftTemplateEntity;
            return convertToEvent(extendedEntry);
        })

        setAllEvents(events);
        // Don't filter here - let the useEffect handle it when both allEvents and selectedDepartment are ready
    };

    const filterEventsByDepartment = (department: { label: string; id: number; } | null, events: EventInput[] | undefined) => {
        if (!events || events.length === 0) {
            return;
        }
        
        calendarRef.current?.getApi().removeAllEvents();
        if (!department) {
            setInitialEvents(events);
        } else {
            const filteredEvents = events.filter((entry: EventInput) =>
                entry.departmentId === department.id
            );
            setInitialEvents(filteredEvents);
        }
    }

    // The department is passed directly here to avoid the synchronisation problems of the setSelectedDepartment
    const handleDepartmentChange = (department: { label: string; id: number; } | null) => {
        setSelectedDepartment(department);
    };

    // Filter events whenever either selectedDepartment or allEvents changes
    // This ensures filtering happens after both are ready
    useEffect(() => {
        if (allEvents && allEvents.length > 0) {
            filterEventsByDepartment(selectedDepartment, allEvents);
        }
    }, [selectedDepartment, allEvents])

    function convertToEvent(entry: CalendarEntryShiftTemplateEntity) {

        return {
            id: String(entry.id),
            start: new Date(entry.startedOn),
            // fullcalendar's end date is exclusive
            end: entry.endedOn,
            allDay: true,
            title: entry.shiftTemplate?.name,
            templateId: entry.templateId,
            departmentId: entry.shiftTemplate?.departmentId,
            backgroundColor: getColorFromId(entry.shiftTemplate?.id ?? 0),
            isAutoGenerated: entry.shiftTemplate?.generatedFromShiftTemplateId ?? 0
        };
    }

    const fetchAllDepartments = async () => {
        const role = checkTokenRole();
        const departments = role === 'DEPCHIEF' ? (await departmentsApi.findWhereIsBoss(seasonId)).data : (await departmentsApi.findAll()).data;
        setDepartments(departments);
        if (departments.length > 0) {
            handleDepartmentChange({
                label: departments[0].name,
                id: departments[0].id
            });
        }
    }

    // Initialize departments on mount and when seasonId changes
    useEffect(() => {
        fetchAllDepartments();
        calendarRef.current?.getApi().removeAllEvents();
        calendarRef.current?.getApi().refetchEvents();
    }, [seasonId]);

    // Fetch data when seasonId changes
    useEffect(() => {
        fetchData();
    }, [seasonId])

    const openDetailView = (clickArg: EventClickArg) => {
        if (binMode) { void requestDelete([clickArg.event], clickArg.view.calendar); return; }
        const event = clickArg.event;
        setViewEventState({
            isOpened: true,
            event
        });
    };

    // Quick "bin" tool. When active, clicking a day (or an entry) deletes the shift-template
    // calendar record(s). Before deleting we dry-run to find any employee shift assignments
    // that would also be removed and, if there are any, show the same confirmation dialog the
    // single-entry delete uses — so we never silently remove assignments.
    const requestDelete = async (events: any[], calendarApi: any) => {
        if (!events.length) return;
        try {
            const dryRuns = await Promise.all(
                events.map(async (ev) => calendarEntriesShiftTemplateApi.dryRunDelete(Number(ev.id)))
            );
            const affected = dryRuns.flatMap((r: any) => (r.data ?? r)?.affectedAssignments ?? []);
            if (affected.length > 0) {
                setPendingDelete({ events, calendarApi, affected });
            } else {
                await deleteEvents(events, calendarApi);
            }
        } catch (error) {
            // If the dry-run fails, fall back to direct delete (same as the single-entry flow).
            console.error('Dry-run delete failed, deleting directly:', error);
            await deleteEvents(events, calendarApi);
        }
    };

    const deleteEvents = async (events: any[], calendarApi: any) => {
        if (!events.length) return;
        const ids = events.map(ev => Number(ev.id)).filter(id => !isNaN(id));
        try {
            await Promise.all(ids.map(async (id) => calendarEntriesShiftTemplateApi.remove(id, seasonId)));
            // Instant feedback + keep state consistent without a full refetch.
            const removed = new Set(ids.map(String));
            events.forEach(ev => calendarApi.getEventById(ev.id)?.remove());
            setAllEvents(prev => (prev ?? []).filter(e => !removed.has(String(e.id))));
        } catch (error) {
            console.error('Failed to delete shift template entries:', error);
            toast.error(t('alerts.error'));
        }
    };

    const handleBinDateClick = (dateArg: DateClickArg) => {
        const calendarApi = dateArg.view.calendar;
        const eventsOnDate = calendarApi.getEvents().filter(
            ev => ev.start?.toDateString() === dateArg.date.toDateString()
        );
        void requestDelete(eventsOnDate, calendarApi);
    };

    const confirmPendingDelete = async () => {
        if (!pendingDelete) return;
        await deleteEvents(pendingDelete.events, pendingDelete.calendarApi);
        setPendingDelete(null);
    };

    const addEventFromDate = (dateArg: DateClickArg) => {
        const eventsOnDate = allEvents?.filter(event => {
            // Event.start has the completed date including time, it is substringed to take only the part obtained from dateArg
            return event.start?.toString().substring(0, 15) === dateArg.date.toDateString();
        });
        const calendarApi = dateArg.view.calendar;
        const events = calendarApi.getEvents();

        const eventExists = events.some((event) => {
            return event.start?.toDateString() === dateArg.date.toDateString();
        });

        if (!eventExists) {

            setAddEventState({
                isOpened: true,
                events: eventsOnDate,
                startedOn: dateArg.date
            });
        }
    };



    return (
        <Box pt={2} pb={4}>
            <Grid container spacing={3}>
                <Grid item md={3} xs={12}>
                    <Autocomplete
                        id="department-selector"
                        options={departments?.map((department) => ({
                            label: department.name,
                            id: department.id
                        })) || []}
                        value={selectedDepartment}
                        onChange={(event, value) => {
                            setSelectedDepartment(value ? { label: value.label, id: value.id } : null);
                            handleDepartmentChange(value ? { label: value.label, id: value.id } : null);
                        }}
                        renderInput={(params) =>
                            <AppTextField
                                {...params}
                                placeholder={t('department.departmentSelector.select')}
                                label={t('department.itemName')}
                            />
                        }
                        fullWidth
                        isOptionEqualToValue={(option, value) => option.id === value?.id} // It serves to prevent alerts/errors mui in the console
                    />
                </Grid>
                <Grid item md={3} xs={12}>
                    <Button
                        style={{
                            height: "3.4rem"
                        }
                        }
                        variant="contained"
                        onClick={() => {
                            setAssignModalState({ isOpened: true });
                            setAllowOverwrite(false);
                        }}
                    >
                        {t('shiftTemplate.assignMultiple')}
                    </Button>
                </Grid>
                <Grid item md={3} xs={12}>
                    <Tooltip title={t('shiftTemplate.bin.tooltip')}>
                        <Button
                            style={{ height: "3.4rem" }}
                            fullWidth
                            color="error"
                            variant={binMode ? "contained" : "outlined"}
                            startIcon={<DeleteSweepIcon />}
                            onClick={() => setBinMode(prev => !prev)}
                        >
                            {binMode ? t('shiftTemplate.bin.active') : t('shiftTemplate.bin.label')}
                        </Button>
                    </Tooltip>
                </Grid>
                <AssignTemplateModalShift
                    originalSelectedDepartment={selectedDepartment}
                    open={assignModalState.isOpened}
                    onClose={() => {
                        setAssignModalState({ isOpened: false });
                    }}
                    fetchCalendarData={fetchData}
                    seasonId={seasonId}
                    allowOverwrite={allowOverwrite}
                />

                <Grid item xs={12}>
                    <Card>
                        <HeaderToolbar
                            date={date}
                            onDatePrev={() => {
                                prevMonth();
                            }}
                            onDateNext={() => {
                                nextMonth();
                            }}
                            onToday={() => {
                                highlightToday();
                            }}
                        />
                        <CalendarWrapper>
                            <FullCalendar
                                timeZone='UTC'
                                eventDisplay='block'
                                initialDate={date}
                                eventClick={openDetailView}
                                dateClick={binMode ? handleBinDateClick : addEventFromDate}
                                plugins={[dayGridPlugin, interactionPlugin]}
                                ref={calendarRef}
                                height={600}
                                rerenderDelay={10}
                                events={initialEvents}
                                headerToolbar={false}
                                firstDay={1}
                                weekNumbers
                                weekNumberCalculation="ISO"
                                weekNumberContent={(arg) => t("shift.weekShort", { n: arg.num })}
                                eventContent={renderEventContent}
                                dayCellContent={renderDayCell}
                                locale={findSelectedLanguage()}
                                dayHeaderFormat={{ weekday: isMobile ? 'narrow' : isTablet ? 'short' : 'long' }}
                                dayMaxEventRows={3}
                                moreLinkClick="popover"
                            />
                        </CalendarWrapper>
                    </Card>
                    {viewEventState.isOpened && (
                        <ViewEventModalShift
                            open={viewEventState.isOpened}
                            onClose={() => {
                                setViewEventState({ isOpened: false });
                            }}
                            fetchCalendarData={fetchData}
                            event={viewEventState.event}
                            departmentId={allEvents?.find(event => event.id === viewEventState.event?.id)?.departmentId}
                        />
                    )}

                    {addEventState.isOpened && (
                        <AddEventModalShift
                            originalSelectedDepartment={selectedDepartment}
                            open={addEventState.isOpened}
                            onClose={() => {
                                setAddEventState({ isOpened: false });
                            }}
                            fetchCalendarData={fetchData}
                            date={addEventState.startedOn}
                        />
                    )}

                    {/* Bin-mode confirmation: warns that employee shift assignments will also be deleted */}
                    <Dialog open={!!pendingDelete} onClose={() => setPendingDelete(null)} maxWidth="sm" fullWidth>
                        <DialogTitle>{t('shift.confirmDeleteTemplate')}</DialogTitle>
                        <DialogContent>
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                <Typography variant="body2">
                                    {t('shift.deleteTemplateWarning', { count: pendingDelete?.affected.length ?? 0 })}
                                </Typography>
                            </Alert>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                                {t('shift.affectedAssignments')}
                            </Typography>
                            <Box sx={{ maxHeight: 300, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}>
                                <List dense>
                                    {(pendingDelete?.affected ?? []).map((a) => (
                                        <ListItem key={a.id}>
                                            <ListItemText
                                                primary={<Typography variant="body2"><strong>{a.employee.firstName} {a.employee.lastName}</strong></Typography>}
                                                secondary={`${new Date(a.date).toLocaleDateString()} · ${a.shiftDetail?.workPosition?.name ?? ''}`}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </Box>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setPendingDelete(null)}>{t('common.cancel')}</Button>
                            <Button color="error" variant="contained" onClick={() => { void confirmPendingDelete(); }}>
                                {t('employees.calendar.entry.deleteEntry')}
                            </Button>
                        </DialogActions>
                    </Dialog>
                </Grid>
            </Grid>
        </Box>
    );
};

export default ShiftsCalendar;
